import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useAdminApiService } from '../hooks/useAdminApiService';
import { setAdminUser } from '../store/slices/authSlice';

/**
 * Redirects non–Super Admin operators without an active admin subscription
 * to the standalone renewal page (subscription APIs stay whitelisted).
 */
const AdminSubscriptionGate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const adminUser = useAppSelector((s) => s.auth.adminUser);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const authLoading = useAppSelector((s) => s.auth.isLoading);
  const token = useAppSelector((s) => s.auth.token);
  const authType = useAppSelector((s) => s.auth.type);
  const adminApi = useAdminApiService();
  const checkingRef = useRef(false);
  const hydrateStartedRef = useRef(false);
  const [isChecking, setIsChecking] = React.useState(true);
  const [hasFullAccess, setHasFullAccess] = React.useState(false);

  /** adminUser is intentionally not persisted (see store whitelist); reload JWT/session without it. */
  const needsAdminProfile =
    !authLoading &&
    isAuthenticated &&
    !adminUser &&
    !!token &&
    (authType === 'JWT' || authType === 'SESSION');

  useEffect(() => {
    if (!needsAdminProfile) {
      hydrateStartedRef.current = false;
      return;
    }
    if (hydrateStartedRef.current) return;
    hydrateStartedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const res = await adminApi.getAdminProfile();
        if (cancelled) return;
        if (res.success && res.data) {
          const p = res.data;
          dispatch(
            setAdminUser({
              id: String(p.id ?? p.username),
              username: p.username,
              email: p.email,
              firstName: p.firstName,
              lastName: p.lastName,
              fullName: p.fullName,
              role: p.role,
              department: p.department,
              status: p.isActive === false ? 'INACTIVE' : 'ACTIVE',
              permissions: p.permissions || [],
              lastLogin: p.lastLoginAt,
              createdDate: p.createdAt || new Date().toISOString(),
              createdBy: 'system',
              avatarBase64: p.avatarBase64,
            })
          );
        } else {
          navigate('/admin/login', { replace: true });
        }
      } catch {
        if (!cancelled) navigate('/admin/login', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needsAdminProfile, adminApi, dispatch, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !adminUser) {
      setIsChecking(false);
      setHasFullAccess(false);
      return;
    }
    if (adminUser.role === 'SUPER_ADMIN') {
      setHasFullAccess(true);
      setIsChecking(false);
      return;
    }
    if (location.pathname === '/admin/subscription-renew' || location.pathname.endsWith('/admin/subscription-renew')) {
      setIsChecking(false);
      setHasFullAccess(false);
      return;
    }

    let cancelled = false;
    (async () => {
      if (checkingRef.current) return;
      checkingRef.current = true;
      setIsChecking(true);
      try {
        const sub = await adminApi.getAdminSubscriptionStatus();
        if (cancelled) return;
        const st = (sub.data as any)?.status;
        const has = (sub.data as any)?.hasAccess;
        if (has === true || st === 'ACTIVE' || st === 'GRACE_PERIOD' || st === 'PLATFORM_OWNER') {
          setHasFullAccess(true);
          setIsChecking(false);
          return;
        }
        setHasFullAccess(false);
        setIsChecking(false);
        navigate('/admin/subscription-renew', { replace: true });
      } catch {
        if (!cancelled) {
          // Fail closed for admin routes with uncertain state.
          setHasFullAccess(false);
          setIsChecking(false);
          navigate('/admin/subscription-renew', { replace: true });
        }
      } finally {
        checkingRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, adminUser, location.pathname, navigate, adminApi]);

  if (authLoading || needsAdminProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zazoo-light-grey px-6">
        <p className="font-nunito text-gray-600">
          {needsAdminProfile ? 'Restoring admin session…' : 'Loading…'}
        </p>
      </div>
    );
  }

  if (!isAuthenticated || !adminUser) {
    return <Navigate to="/admin/login" replace />;
  }

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zazoo-light-grey px-6">
        <p className="font-nunito text-gray-600">Checking subscription access…</p>
      </div>
    );
  }

  if (!hasFullAccess && !location.pathname.endsWith('/admin/subscription-renew')) {
    return <Navigate to="/admin/subscription-renew" replace />;
  }

  return <Outlet />;
};

export default AdminSubscriptionGate;
